import cn from 'clsx'
import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react'
import { formatDate } from '@luxuryescapes/lib-refunds'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import PackageOptions from './LastMinutePackageOptions'

import ScrollAnchor from 'components/Common/ScrollAnchor'
import OfferIntro from './LastMinuteIntro'
import { fetchOfferFlightPrice } from 'actions/OfferActions'

import config from 'constants/config'
import Breakpoint from 'components/Common/Breakpoint'
import LastMinuteStickyHeader from './LastMinuteStickyHeader'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import LayoutContainer from 'components/Common/LayoutContainer'
import Backdrop from 'components/utils/Backdrop'
import LastMinuteSidebarFilters from './LastMinuteSidebarFilters'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { INITIAL_OFFER_PAGE_STATE, OfferPageActions, OfferPageDispatchAction, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import { clearBookingCalendar, fetchCalendars } from 'actions/CalendarActions'
import usePrevious from 'hooks/usePrevious'
import { replace, RouterLocation } from 'connected-react-router'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { urlHashDatePicker } from 'constants/url'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import WalledContent from 'components/Common/WalledContent'
import Heading from 'components/Luxkit/Typography/Heading'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import { generateRoomOccupants, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import MultipleRoomsBookingContent from '../MultipleRoomsBooking/MultipleRoomsBookingContent'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { multiRoomEnabled } from 'selectors/featuresSelectors'

import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import * as Analytics from 'analytics/analytics'
import { offerPageGuestSelectionEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import AlwaysOnAndLMEOfferPageStickyFooter from '../AlwaysOn/AlwaysOnAndLMEOfferPageStickyFooter'
import OfferPageFlightDealBanner from '../OfferPageFlightDealBanner'
import ModalProvider from 'contexts/ModalProvider'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { getNextFilterWithCapacityCheckInMiddlware } from '../utils'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import { encodeSearchParams } from 'lib/search/searchUtils'
import moment from 'moment'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { EmptyArray } from 'lib/array/arrayUtils'

const RelativeBlock = styled.div`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const middlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddlware]

interface Props {
  defaults: Partial<OfferPageState>;
  offer: App.HotelOffer;
  originAirports?: Array<App.Airport>;
  airports?: Array<App.Airport>;
  routerLocation: RouterLocation<unknown>;
  isMultiRoomEnabled: boolean;
  routeHistory: App.RouteHistoryState;
}

const fallbackRooms = [generateRoomOccupants()]

function LastMinuteOfferContent(props: Props) {
  const {
    offer,
    originAirports = EmptyArray,
    airports = EmptyArray,
    defaults,
    routerLocation,
    isMultiRoomEnabled,
    routeHistory,
  }: Props = props

  const {
    id,
    packages,
  } = offer

  // LastMinute only loads the current month and maybe the next month in some cases
  const currentDate = formatDate.convertDateByTimezone(new Date(), offer.property.timezone ?? '')
  const defaultMonth = `${formatDate.format(currentDate, 'm')}-${formatDate.format(currentDate, 'yyyy')}`

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!defaults.checkInDate)
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
  }, middlewares)

  const {
    airport,
    duration,
    activeFilter,
    rooms = fallbackRooms,
    package: selectedPackage,
    checkInDate,
  } = state

  const dispatch = useAppDispatch()

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousState = usePrevious<OfferPageState>(state, INITIAL_OFFER_PAGE_STATE)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const lastFetchedAirport = useRef<App.Airport | null>(null)

  const checkInMoment = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOutDate = useMemo(() => checkInMoment?.clone().add(duration, 'days').format(ISO_DATE_FORMAT), [checkInMoment, duration])

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: routerLocation.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
    }).toString(), [checkInDate, checkOutDate, rooms, routerLocation.search])

  useSyncHotelTripItem(state, offer)

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
    previousState.rooms !== rooms ||
    previousState.checkInDate !== checkInDate
  }, [state.rooms, state.checkInDate])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToOptions = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      scrollToPackageOptions()
    }
  }, [isTabletScreenSizeAndUp, scrollToPackageOptions])

  const fetchCalendarData = () => {
    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null

    dispatch(fetchCalendars(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: [(selectedPackage as App.HotelPackage) ?? offer.lowestPricePackage],
    }))
    dispatch(fetchCalendars(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: offer.packages,
    }))
  }

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    const hasDate = !!checkInDate
    if (previousFilter === 'capacity' &&
        !hasRooms &&
        !activeFilter
    ) {
      autoScrollToOptions()
      scrollAfterFilterClose.current = false
    }

    if (activeFilter) {
      autoScrollToOptions()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (state.room) {
          window.location.hash = state.room.id
        } else {
          autoScrollToOptions()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else if (viewedDates && (lastFetchedAirport.current !== airport || lastFetchedRooms.current !== rooms)) {
      // rooms/airport has changed, all loaded calendars are no longer valid as we load calendars for a specific
      // combinations of flight origin (flight prices) and rooms (availability), so clear them
      dispatch(clearBookingCalendar())
      fetchCalendarData()
    }
  }, [activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const hotLead = useSaveHotLead({
    offerId: id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration,
    occupancy: rooms,
    defaultOccupancy: fallbackRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, state))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useMountedEffect(() => {
    if (state.rooms) {
      Analytics.trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToOptions()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (routerLocation.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...routerLocation, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return packages.flatMap((p) => p.roomRate.capacities)
  }, [packages])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy && config.businessTraveller.currentAccountMode !== 'business'
  const availableForPurchase = useIsAvailableForPurchase(state)
  const showDealBanner = airport && destinationAirport && availableForPurchase

  const canBookMultiRoomTypes = isMultiRoomEnabled && rooms.length > 1

  // Need to pass the context state above the tree
  useEffect(() => {
    if (state.offer) {
      dispatch({
        type: SET_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer.id,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration,
          rooms,
        },
      })
    }
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer?.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate, duration, rooms, state.offer])

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  // Do not show the Expired recommendation when it comes from my escapes
  const prevPath = routeHistory.prevPath
  const shouldShowLere = !prevPath.includes('/my-escapes/order')

  return (
    <OfferPageStateProvider state={state} dispatch={stateDispatch}>
      <ImmersiveSaveButtonPortal>
        <BookmarkButton offer={offer} />
      </ImmersiveSaveButtonPortal>
      <TripGuard>
        <SavePromptHotel offer={offer} />
      </TripGuard>
      <RelativeBlock>
        <div>
          <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
          <CSSBreakpoint min="tablet">
            <LastMinuteStickyHeader
              offer={offer}
              onViewPackageOptions={scrollToPackageOptions}
            />
          </CSSBreakpoint>
          {shouldShowLere && <ExpiredDealsLereRecommendations
            hideOffers={false}
          />}
          {availableForPurchase && (
            <WalledContent enforced={offer.walledGarden}>
              <Breakpoint max="mobile">
                <ModalProvider>
                  <LastMinuteSidebarFilters
                    showFlightInformation={showFlightInformation}
                    defaultDayPicker
                    defaultMonth={defaultMonth}
                    onViewPackageOptions={scrollToPackageOptions}
                    capacities={occupancyLimits}
                  />
                </ModalProvider>
              </Breakpoint>
            </WalledContent>
          )}
          <OfferPageGallery offer={offer} autoPlayVideo={!!offer.isVideoVimeoHero} />
          <Pane>
            <OfferIntro
              onViewPackageOptions={autoScrollToOptions}
              flightDestination={destinationAirport}
              queryString={queryString}
            />
          </Pane>
        </div>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <PackageOptionsPane background="neutral-six" id="Package-Options">
          <LayoutContainer>
            <VerticalSpacer gap={32}>
              <VerticalSpacer gap={16}>
                <Heading variant="heading2" format="titlecase" as="h2" align="center">
                  {config.brandConfig.travelSynonym} Options
                </Heading>
                {availableForPurchase && (
                  <WalledContent enforced={offer.walledGarden}>
                    <CSSBreakpoint max="mobile">
                      <Group direction="vertical" gap={8}>
                        <OfferCapacityWithAgesSelectBox />
                        <OfferCalendarDatePickerSelectBox />
                        {showFlightInformation && <OfferAirportSelectBox />}
                      </Group>
                    </CSSBreakpoint>
                    <CSSBreakpoint min="tablet">
                      <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                        <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                        <OfferCalendarDatePickerSelect
                          boundaryRef={desktopFiltersWrapperRef}
                          defaultDayPicker
                          defaultMonth={defaultMonth}
                        />
                        {showFlightInformation && <OfferAirportSelect />}
                      </DesktopFilterBlock>
                    </CSSBreakpoint>

                  </WalledContent>
                )}

                {showDealBanner && config.FLIGHT_ENABLED && <OfferPageFlightDealBanner
                  destinationAirportCode={destinationAirport.code}
                  originAirportCode={airport.code}
                />}
              </VerticalSpacer>
            </VerticalSpacer>
          </LayoutContainer>
        </PackageOptionsPane>
        {availableForPurchase &&
          <AlwaysOnAndLMEOfferPageStickyFooter
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
          }
        {packages.length > 0 && !canBookMultiRoomTypes && (
          <PackageOptions
            showFlightInformation={showFlightInformation}
            destinationAirport={destinationAirport}
          />
        )}
        {canBookMultiRoomTypes && (
          <MultipleRoomsBookingContent />
        )}
      </RelativeBlock>
    </OfferPageStateProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    originAirports: state.geo.originAirports,
    airports: state.geo.airports,
    defaults: getInitialLeOfferPageState(state, ownProps.offer!),
    routerLocation: state.router.location,
    isMultiRoomEnabled: multiRoomEnabled(state, ownProps.offer!),
    routeHistory: state.routeHistory,
  }
}

export default connect(mapStateToProps)(LastMinuteOfferContent)
